import { useReactiveVar } from '@apollo/client';
import { LockForm } from 'components/Drawer/types';
import { Value } from 'components/Multiselect/Multiselect';
import dayjs from 'dayjs';
import { DoorBatteryWarningLevel, DoorType } from 'graphql/generated/graphqlTypes';
import useDoorsAndLocations from 'hooks/useDoorsAndLocations/useDoorsAndLocations';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { sortBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EditLockDrawer } from 'state/types';
import { authVar } from 'state/vars';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import LockUI from 'ui/organisms/Drawer/variants/Lock';

const getInputFormatValue = (value?: string | undefined) =>
  value ? dayjs(`${dayjs().format('YYYY-MM-DD')} ${value}`) : null;

const EditLockWithGroups: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const isEntraLess = user?.isEntraLess!;
  const { register, handleSubmit, setValue, control } = useForm<LockForm>();
  const { drawerState } = useDrawer<EditLockDrawer>();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { hideDrawer } = useDrawer();
  const {
    updateDoorWithLocations,
    updateDoorWithLocationsLoading,
    deleteDoorWithLocationsLoading,
    doorsWithLocationsLoading,
    doorsWithLocations,
    locationsWithDoorsLoading,
    locationsWithDoors
  } = useDoorsAndLocations({
    handleFetchError
  });
  const [nameInputValue, setNameInputValue] = useState<string>();
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [switchIsOfficeModeEnabled, setSwitchIsOfficeModeEnabled] = useState<boolean>();
  const [inputOfficeModeFromValue, setInputOfficeModeFromValue] = useState<dayjs.Dayjs | null>(
    getInputFormatValue(drawerState.contentValue.officeModeFromValue)
  );
  const [inputOfficeModeToValue, setInputOfficeModeToValue] = useState<dayjs.Dayjs | null>(
    getInputFormatValue(drawerState.contentValue.officeModeToValue)
  );
  const [officeModeTimeInputValidation, setOfficeModeTimeInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);

  const getGroupsDefaultValue = () =>
    !locationsWithDoorsLoading && locationsWithDoors && drawerState.contentValue.groupIds
      ? drawerState.contentValue.groupIds.map((location) => ({ label: location.name, value: location.id }))
      : [];

  const [groupsMultiselect, setGroupsMultiselect] = useState<Array<Value>>(getGroupsDefaultValue());
  const isLoading =
    locationsWithDoorsLoading ||
    doorsWithLocationsLoading ||
    updateDoorWithLocationsLoading ||
    deleteDoorWithLocationsLoading;

  const onSubmit = async () => {
    setShowValidation(true);
    if (
      !isLoading &&
      nameInputValue &&
      nameInputValidation === undefined &&
      officeModeTimeInputValidation === undefined
    ) {
      await updateDoorWithLocations(
        drawerState?.contentValue?.doorId || '',
        nameInputValue,
        groupsMultiselect.map((item) => item.value),
        switchIsOfficeModeEnabled ? dayjs(inputOfficeModeFromValue).format('HH:mm:ss') : undefined,
        switchIsOfficeModeEnabled ? dayjs(inputOfficeModeToValue).format('HH:mm:ss') : undefined
      );
      hideDrawer();
    }
  };

  useEffect(() => {
    if (drawerState?.contentValue) {
      if (nameInputValue === undefined) setNameInputValue(drawerState.contentValue.name);
      if (switchIsOfficeModeEnabled === undefined)
        setSwitchIsOfficeModeEnabled(drawerState.contentValue.isOfficeModeEnabled);
      if (inputOfficeModeFromValue === undefined)
        setInputOfficeModeFromValue(dayjs(drawerState.contentValue.officeModeFromValue) || dayjs().hour(7).minute(0));
      if (inputOfficeModeToValue === undefined)
        setInputOfficeModeToValue(dayjs(drawerState.contentValue.officeModeToValue) || dayjs().hour(22).minute(0));
    }
  }, [drawerState, inputOfficeModeFromValue, inputOfficeModeToValue, nameInputValue, switchIsOfficeModeEnabled]);
  const handleNameInputOnChange = (value: string) => {
    setNameInputValue(value);
    setNameInputValidation(undefined);
    if (doorsWithLocations.findIndex((door) => door.name === value) !== -1 && value !== drawerState.contentValue.name)
      setNameInputValidation(translation.name_must_be_unique);
    if (value === '') setNameInputValidation(translation.name_cannot_be_empty);
  };

  const handleSwitchIsOfficeModeEnabledOnChange = () => {
    setSwitchIsOfficeModeEnabled(!switchIsOfficeModeEnabled);
  };

  const handleGroupsMultiselectChange = (event: unknown, value: Value[]) => {
    setGroupsMultiselect(value);
  };

  const handleGroupsMultiselectClear = () => {
    setGroupsMultiselect([]);
  };

  const isCorrectTimeSetForDay = useCallback(
    () => Boolean(dayjs(inputOfficeModeFromValue).isBefore(dayjs(inputOfficeModeToValue))),
    [inputOfficeModeFromValue, inputOfficeModeToValue]
  );

  const isOfficeModeAvailable = !!drawerState.contentValue.type && drawerState.contentValue.type !== DoorType.Tanlock;

  const getGroupsOptions = () =>
    !locationsWithDoorsLoading && locationsWithDoors
      ? sortBy(locationsWithDoors, (location) => location.name.toLocaleLowerCase()).map(
          (location) => ({ label: location.name, value: location.id }) as Value
        )
      : [];

  useEffect(() => {}, [doorsWithLocations, drawerState.contentValue.name, nameInputValue]);

  useEffect(() => {
    if (switchIsOfficeModeEnabled && !isCorrectTimeSetForDay())
      setOfficeModeTimeInputValidation(translation.start_time_cannot_be_after_end_time);
    else setOfficeModeTimeInputValidation(undefined);
  }, [isCorrectTimeSetForDay, switchIsOfficeModeEnabled, translation.start_time_cannot_be_after_end_time]);

  return (
    <DrawerTemplate
      title={translation.edit_lock}
      confirmButtonText={translation.save_changes}
      closeButtonText={translation.cancel}
      closeButton
      id="drawer-edit-lock"
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isLoading}
      disableConfirmButton={
        showValidation &&
        (nameInputValidation !== undefined || officeModeTimeInputValidation !== undefined) &&
        isLoading
      }
    >
      <LockUI
        defaultValues={drawerState?.contentValue}
        register={register}
        nameInputValue={nameInputValue || ''}
        handleNameInputOnChange={handleNameInputOnChange}
        control={control}
        isOfficeModeAvailable={isOfficeModeAvailable}
        switchIsOfficeModeEnabled={switchIsOfficeModeEnabled}
        handleSwitchIsOfficeModeEnabledOnChange={handleSwitchIsOfficeModeEnabledOnChange}
        inputOfficeModeFromValue={inputOfficeModeFromValue}
        handleInputOfficeModeFromValueOnChange={setInputOfficeModeFromValue}
        inputOfficeModeToValue={inputOfficeModeToValue}
        handleInputOfficeModeToValueOnChange={setInputOfficeModeToValue}
        showValidation={showValidation}
        nameInputValidation={nameInputValidation}
        officeModeTimeInputValidation={officeModeTimeInputValidation}
        batteryStatus={drawerState?.contentValue.batteryWarningLevel || DoorBatteryWarningLevel.BatteryUnknown}
        groupsOptions={getGroupsOptions()}
        handleGroupsOnChange={handleGroupsMultiselectChange}
        groupsMultiselect={groupsMultiselect}
        handleGroupsMultiselectClear={handleGroupsMultiselectClear}
      />
    </DrawerTemplate>
  );
};
export default EditLockWithGroups;
